/**
 * Logger Utility
 *
 * Provides logging functionality that:
 * - Always logs in development
 * - Hides logs in production by default
 * - Can be enabled in production via Gladly._showLogging()
 */

// Track whether logging is enabled (always true in dev, can be toggled in prod)
let loggingEnabled = import.meta.env.DEV;

/**
 * Enable logging in production (useful for debugging)
 * Can be called from console: Gladly._showLogging()
 */
export function enableLogging(): void {
  loggingEnabled = true;
  console.log('[chat-v2]', 'Logging enabled');
}

/**
 * Disable logging (only affects production)
 */
export function disableLogging(): void {
  if (!import.meta.env.DEV) {
    loggingEnabled = false;
  }
}

/**
 * Whether a log call will actually print. Exported so a caller that has to build
 * its arguments — scrubbing a payload, say — can skip the work when nothing
 * would come of it.
 */
export function isLoggingEnabled(): boolean {
  return shouldLog();
}

function shouldLog(): boolean {
  // Always log in development
  if (import.meta.env.DEV) {
    return true;
  }
  // In production, only log if explicitly enabled
  return loggingEnabled;
}

/**
 * Prepend [chat-v2] prefix to log arguments
 */
function prependPrefix(args: unknown[]): unknown[] {
  return ['[chat-v2]', ...args];
}

/**
 * Logger object that mimics console methods
 * Automatically prepends [chat-v2] prefix to all log messages
 */
export const logger = {
  log: (...args: unknown[]): void => {
    if (shouldLog()) {
      console.log(...prependPrefix(args));
    }
  },

  warn: (...args: unknown[]): void => {
    if (shouldLog()) {
      console.warn(...prependPrefix(args));
    }
  },

  error: (...args: unknown[]): void => {
    // Always show errors, even in production
    console.error(...prependPrefix(args));
  },

  info: (...args: unknown[]): void => {
    if (shouldLog()) {
      console.info(...prependPrefix(args));
    }
  },

  debug: (...args: unknown[]): void => {
    if (shouldLog()) {
      console.debug(...prependPrefix(args));
    }
  },
};
